/* Sun Brims theme: client-side interactions
 *
 * Sections:
 *   1. Announcement rotator
 *   2. Scroll-aware header
 *   3. Search overlay
 *   4. Mobile menu + accordions
 *   5. Mega menu hover
 *   6. Email / phone deobfuscation
 *   7. Newsletter discount modal (POST /shop/discount → 10% code)
 *   8. Native Shopify cart drawer (using /cart/*.js)
 *   9. Judge.me rating injection (populates [data-avg-rating])
 */

(function () {
  'use strict';

  const SB = (window.SunBrims = window.SunBrims || {});
  const API_BASE = SB.apiBaseUrl || 'https://australia-southeast1-sun-brims.cloudfunctions.net/api';
  const FREE_SHIP = SB.freeShippingThreshold || 200;
  const ROUTES = SB.routes || {
    cart: '/cart',
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartUpdate: '/cart/update.js',
    cartClear: '/cart/clear.js',
  };

  // ── 1. Announcement rotator ──────────────────────────────────────
  function initAnnouncementRotator() {
    const bar = document.getElementById('announcement-bar');
    if (!bar) return;
    let announcements;
    try { announcements = JSON.parse(bar.dataset.announcements || '[]'); } catch { return; }
    if (announcements.length < 2) return;

    const iconEl = document.getElementById('announcement-icon');
    const textEl = document.getElementById('announcement-text');
    let i = 0;
    setInterval(() => {
      i = (i + 1) % announcements.length;
      if (iconEl) iconEl.textContent = announcements[i].icon;
      if (textEl) textEl.textContent = announcements[i].text;
    }, 4000);
  }

  // ── 2. Welcome offer bar (SBA V7) ────────────────────────────────
  function initOfferBar() {
    const bar = document.querySelector('[data-offer-bar]');
    if (!bar) return;
    let dismissed = false;
    try { dismissed = sessionStorage.getItem('v7-offer-dismissed') === '1'; } catch {}
    if (dismissed) return;
    bar.classList.remove('hidden');

    bar.querySelectorAll('[data-offer-dismiss]').forEach(btn => {
      btn.addEventListener('click', () => {
        bar.classList.add('hidden');
        try { sessionStorage.setItem('v7-offer-dismissed', '1'); } catch {}
      });
    });

    // Mobile: "Get 10% off" reveals the email form
    const openBtn = bar.querySelector('[data-offer-open]');
    const mobileWrap = bar.querySelector('[data-offer-mobile-form]');
    openBtn?.addEventListener('click', () => {
      mobileWrap?.classList.toggle('hidden');
      mobileWrap?.querySelector('input')?.focus();
    });

    // Mobile form proxies to the main newsletter form (same discount flow)
    const mobileForm = bar.querySelector('[data-offer-form-mobile]');
    mobileForm?.addEventListener('submit', e => {
      e.preventDefault();
      const email = bar.querySelector('[data-offer-email-mobile]')?.value.trim();
      const mainInput = document.getElementById('newsletter-email');
      const mainForm = document.getElementById('newsletter-form');
      if (!email || !mainInput || !mainForm) return;
      mainInput.value = email;
      mainForm.requestSubmit ? mainForm.requestSubmit() : mainForm.dispatchEvent(new Event('submit', { cancelable: true }));
    });
  }

  // ── 3. Search overlay ────────────────────────────────────────────
  function initSearchOverlay() {
    const toggle = document.getElementById('search-toggle');
    const overlay = document.getElementById('search-overlay');
    const input = document.getElementById('search-input');
    const icon = document.getElementById('search-icon');
    const closeIcon = document.getElementById('search-close-icon');
    if (!toggle || !overlay) return;

    toggle.addEventListener('click', () => {
      const wasHidden = overlay.classList.contains('hidden');
      overlay.classList.toggle('hidden');
      icon?.classList.toggle('hidden', wasHidden);
      closeIcon?.classList.toggle('hidden', !wasHidden);
      if (wasHidden) {
        SB.closeFitDrawer?.();
        input?.focus();
      }
    });
  }

  // ── 3b. Helmet fit search (header drawer + inline checkers) ──────
  function initFitSearch() {
    const dataEl = document.getElementById('helmet-data');
    if (!dataEl) return;
    let HELMETS = [];
    try { HELMETS = JSON.parse(dataEl.textContent || '[]'); } catch { return; }
    if (!HELMETS.length) return;

    const norm = s => (s || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
    const matches = (h, q) => q.split(' ').every(w => norm(h.brand + ' ' + h.model + ' ' + (h.aliases || '')).includes(w));
    const esc = s => { const d = document.createElement('div'); d.textContent = s || ''; return d.innerHTML; };

    function badge(h) {
      if (!h.fits || !h.fits.length) return '<span class="inline-block border border-fieldline text-inksoft rounded-full px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap">✕ Not compatible</span>';
      const names = h.fits.map(f => f.name).join(' ');
      let partial;
      if (h.category === 'cycling' || h.category === 'kids') partial = !/Cycling Brim/.test(names);
      else if (h.category === 'equestrian') partial = !/Rezzo/.test(names);
      else partial = /friction/i.test(h.fit_note || '');
      return `<span class="inline-block bg-sun text-ink rounded-full px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap">✓ ${partial ? 'Fits (see note)' : 'Fits'}</span>`;
    }

    function row(h, fitUrl) {
      const q = encodeURIComponent(h.brand + ' ' + h.model);
      const thumb = h.image
        ? `<span class="w-11 h-11 rounded-lg bg-cream shrink-0 flex items-center justify-center overflow-hidden"><img src="${esc(h.image)}" alt="" loading="lazy" class="w-10 h-10 object-contain mix-blend-multiply"></span>`
        : `<span class="w-11 h-11 rounded-lg bg-cream shrink-0"></span>`;
      const works = (h.fits || []).map(f => esc(f.name)).join(', ');
      return `<a href="${fitUrl}?q=${q}" class="flex items-center gap-3 px-3 py-2.5 hover:bg-cream transition-colors">
        ${thumb}
        <span class="min-w-0 flex-1">
          <span class="block font-semibold text-sm truncate">${esc(h.brand)} ${esc(h.model)}</span>
          <span class="block text-[12px] text-inksoft truncate">${works ? 'Works with: ' + works : esc(h.fit_note || '')}</span>
        </span>
        ${badge(h)}
      </a>`;
    }

    function renderResults(resultsEl, value, fitUrl) {
      const q = norm(value);
      if (!q || q.length < 2) { resultsEl.innerHTML = ''; resultsEl.classList.add('hidden'); return; }
      const list = HELMETS.filter(h => matches(h, q)).slice(0, 5);
      resultsEl.classList.remove('hidden');
      resultsEl.innerHTML = list.length
        ? `<div class="divide-y divide-hairline">${list.map(h => row(h, fitUrl)).join('')}</div>`
        : `<div class="px-4 py-3.5 text-sm text-inksoft">No match for “${esc(value.trim())}”. It may still fit. <a href="${fitUrl}?q=${encodeURIComponent(value.trim())}" class="font-semibold text-ink underline underline-offset-2">Check the rules</a> or <a href="/pages/contact" class="font-semibold text-ink underline underline-offset-2">ask us</a>.</div>`;
    }

    // Header drawer
    const toggle = document.getElementById('fit-toggle');
    const overlay = document.getElementById('fit-overlay');
    const drawerForm = document.getElementById('fit-drawer-form');
    const drawerInput = document.getElementById('fit-drawer-input');
    const drawerResults = document.getElementById('fit-drawer-results');
    if (toggle && overlay && drawerInput && drawerResults) {
      const fitUrl = overlay.dataset.fitUrl || '/pages/helmet-compatibility';

      function open() {
        // close the search overlay if it's open (via its own toggle so icons reset)
        const so = document.getElementById('search-overlay');
        if (so && !so.classList.contains('hidden')) document.getElementById('search-toggle')?.click();
        overlay.classList.remove('hidden');
        drawerInput.focus();
      }
      function close() { overlay.classList.add('hidden'); }

      toggle.addEventListener('click', e => {
        e.preventDefault();
        overlay.classList.contains('hidden') ? open() : close();
      });
      drawerInput.addEventListener('input', () => renderResults(drawerResults, drawerInput.value, fitUrl));
      drawerForm?.addEventListener('submit', e => {
        e.preventDefault();
        const v = drawerInput.value.trim();
        window.location.href = v ? `${fitUrl}?q=${encodeURIComponent(v)}` : fitUrl;
      });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
      document.addEventListener('click', e => {
        if (!overlay.classList.contains('hidden') && !overlay.contains(e.target) && !toggle.contains(e.target)) close();
      });

      // let the search overlay close us too
      SB.closeFitDrawer = close;
    }

    // Inline fit checkers (homepage section). Submit falls through to the
    // form's native GET (?q=) to the fit page; typing searches in place.
    document.querySelectorAll('form[data-fit-checker]').forEach(form => {
      const input = form.querySelector('[name="q"]');
      const resultsEl = form.parentElement?.querySelector('[data-fit-results]');
      const fitUrl = form.getAttribute('action') || '/pages/helmet-compatibility';
      if (!input || !resultsEl) return;
      input.addEventListener('input', () => renderResults(resultsEl, input.value, fitUrl));
      document.addEventListener('click', e => {
        if (!resultsEl.contains(e.target) && e.target !== input) resultsEl.classList.add('hidden');
      });
    });
  }

  // ── 4. Mobile menu ───────────────────────────────────────────────
  function initMobileMenu() {
    const toggle = document.getElementById('mobile-toggle');
    const overlay = document.getElementById('mobile-overlay');
    const close = document.getElementById('mobile-close');
    const backdrop = document.getElementById('mobile-backdrop');
    const menuIcon = document.getElementById('menu-icon');
    const closeIcon = document.getElementById('close-icon');
    if (!toggle || !overlay) return;

    function open() {
      overlay.classList.remove('hidden');
      menuIcon?.classList.add('hidden');
      closeIcon?.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    }
    function shut() {
      overlay.classList.add('hidden');
      menuIcon?.classList.remove('hidden');
      closeIcon?.classList.add('hidden');
      document.body.style.overflow = '';
    }
    toggle.addEventListener('click', () => overlay.classList.contains('hidden') ? open() : shut());
    close?.addEventListener('click', shut);
    backdrop?.addEventListener('click', shut);

    document.querySelectorAll('.mobile-accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const target = trigger.getAttribute('data-target');
        const content = document.querySelector(`[data-content="${target}"]`);
        const chevron = trigger.querySelector('.mobile-chevron');
        if (content) {
          content.classList.toggle('hidden');
          chevron?.classList.toggle('rotate-180');
        }
      });
    });
  }

  // ── 5. Mega menu hover ───────────────────────────────────────────
  function initMegaMenu() {
    document.querySelectorAll('.mega-trigger').forEach(trigger => {
      const title = trigger.getAttribute('data-mega');
      const panel = document.querySelector(`[data-mega-panel="${title}"]`);
      const chevron = trigger.querySelector('.mega-chevron');
      let timeout;

      function show() {
        clearTimeout(timeout);
        document.querySelectorAll('.mega-panel').forEach(p => p.classList.add('hidden'));
        document.querySelectorAll('.mega-chevron').forEach(c => c.classList.remove('rotate-180'));
        document.querySelectorAll('.mega-trigger button').forEach(b => {
          b.classList.remove('text-foreground', 'bg-muted');
          b.classList.add('text-muted-foreground');
        });
        panel?.classList.remove('hidden');
        chevron?.classList.add('rotate-180');
        const btn = trigger.querySelector('button');
        btn?.classList.add('text-foreground', 'bg-muted');
        btn?.classList.remove('text-muted-foreground');
      }
      function hide() {
        timeout = setTimeout(() => {
          panel?.classList.add('hidden');
          chevron?.classList.remove('rotate-180');
          const btn = trigger.querySelector('button');
          btn?.classList.remove('text-foreground', 'bg-muted');
          btn?.classList.add('text-muted-foreground');
        }, 200);
      }

      trigger.addEventListener('mouseenter', show);
      trigger.addEventListener('mouseleave', hide);
      panel?.addEventListener('mouseenter', () => clearTimeout(timeout));
      panel?.addEventListener('mouseleave', hide);
    });
  }

  // ── 6. Email/phone deobfuscation ─────────────────────────────────
  function deobfuscate() {
    document.querySelectorAll('.obf-email').forEach(el => {
      const u = el.dataset.u, d = el.dataset.d;
      if (!u || !d) return;
      const addr = `${u}@${d}`;
      const subject = el.dataset.subject;
      el.href = `mailto:${addr}${subject ? `?subject=${subject}` : ''}`;
      el.textContent = addr;
    });
    document.querySelectorAll('.obf-email-text').forEach(el => {
      const u = el.dataset.u, d = el.dataset.d;
      if (!u || !d) return;
      el.textContent = `${u}@${d}`;
    });
    document.querySelectorAll('.obf-phone').forEach(el => {
      const cc = el.dataset.cc, num = el.dataset.num, fmt = el.dataset.fmt;
      if (!cc || !num) return;
      el.href = `tel:${cc}${num}`;
      el.textContent = fmt || `${cc}${num}`;
    });
  }

  // ── 7. Newsletter discount modal ─────────────────────────────────
  function initNewsletter() {
    const form = document.getElementById('newsletter-form');
    const emailInput = document.getElementById('newsletter-email');
    const btn = document.getElementById('newsletter-btn');
    const modal = document.getElementById('discount-modal');
    const overlay = document.getElementById('discount-overlay');
    const closeBtn = document.getElementById('discount-close');
    const codeEl = document.getElementById('discount-code');
    const copyBtn = document.getElementById('discount-copy');
    const expiryEl = document.getElementById('discount-expiry');
    const stepGen = document.getElementById('step-generating');
    const stepSucc = document.getElementById('step-success');
    const stepErr = document.getElementById('step-error');
    const errMsg = document.getElementById('error-message');
    const retryBtn = document.getElementById('error-retry');
    const checks = [document.getElementById('check-1'), document.getElementById('check-2'), document.getElementById('check-3')];
    const status = document.getElementById('step-status');
    if (!form || !modal) return;

    let lastEmail = '';

    function tick(el) {
      el.style.borderColor = 'hsl(var(--primary))';
      el.style.backgroundColor = 'hsl(var(--primary))';
      el.style.color = 'hsl(var(--primary-foreground))';
      el.innerHTML = '✓';
    }
    function resetChecks() {
      checks.forEach(c => { if (c) { c.style.borderColor = c.style.backgroundColor = c.style.color = ''; c.innerHTML = ''; } });
    }
    function show(step) {
      stepGen?.classList.add('hidden'); stepSucc?.classList.add('hidden'); stepErr?.classList.add('hidden');
      step?.classList.remove('hidden');
    }
    function showModal() { modal.classList.remove('hidden'); modal.classList.add('flex'); }
    function hideModal() { modal.classList.add('hidden'); modal.classList.remove('flex'); }
    const wait = ms => new Promise(r => setTimeout(r, ms));

    overlay?.addEventListener('click', hideModal);
    closeBtn?.addEventListener('click', hideModal);
    copyBtn?.addEventListener('click', () => {
      navigator.clipboard.writeText(codeEl?.textContent || '').then(() => {
        copyBtn.textContent = '✓ Copied!';
        setTimeout(() => copyBtn.textContent = 'Copy Code', 2000);
      });
    });
    retryBtn?.addEventListener('click', () => generateCode(lastEmail));

    async function generateCode(email) {
      resetChecks(); show(stepGen); showModal();
      try {
        if (status) status.textContent = 'Verifying your email';
        await wait(600); tick(checks[0]);

        if (status) status.textContent = 'Generating unique discount code';
        const fetchP = fetch(API_BASE + '/shop/discount', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        await wait(800); tick(checks[1]);

        if (status) status.textContent = 'Activating 10% off in store';
        const res = await fetchP;
        const data = await res.json();
        if (data.error) throw new Error(data.error);

        await wait(500); tick(checks[2]);
        if (status) status.textContent = 'Done!';
        await wait(400);

        localStorage.setItem('cart-discount-code', data.code);
        localStorage.setItem('cart-discount-claimed', '1');
        if (SB.cart && SB.cart.applyDiscount) {
          try { SB.cart.applyDiscount(data.code); } catch {}
        }

        if (codeEl) codeEl.textContent = data.code;
        if (expiryEl && data.expiresAt) {
          const d = new Date(data.expiresAt);
          expiryEl.textContent = 'Expires at ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        show(stepSucc);
        if (emailInput) emailInput.value = '';
      } catch (err) {
        console.error('Discount generation failed:', err);
        if (errMsg) errMsg.textContent = err.message || 'Please try again in a moment.';
        show(stepErr);
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = 'Subscribe'; }
      }
    }

    form.addEventListener('submit', e => {
      e.preventDefault();
      const email = emailInput?.value.trim();
      if (!email) return;
      lastEmail = email;
      if (btn) { btn.disabled = true; btn.textContent = 'Creating…'; }
      generateCode(email);
    });
  }

  // ── 8. Cart drawer (native /cart/*.js) ───────────────────────────
  const cart = (function () {
    let state = { items: [], item_count: 0, total_price: 0, currency: SB.shopCurrency || 'AUD', items_subtotal_price: 0 };
    let drawerOpen = false;
    let isLoading = false;

    async function fetchCart() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }});
      state = await res.json();
      return state;
    }

    async function addItem({ variantId, quantity = 1 }) {
      if (isLoading) return;
      isLoading = true; render();
      try {
        const res = await fetch(ROUTES.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
        });
        const result = await res.json();
        if (!res.ok || result.status) {
          console.error('Cart add failed:', result);
          return;
        }
        await fetchCart();
        const added = result.items?.[0] || result;
        open();

        // Meta Pixel AddToCart
        if (typeof fbq === 'function') {
          fbq('track', 'AddToCart', {
            content_name: added.product_title || added.title,
            content_ids: [String(variantId)],
            content_type: 'product',
            value: (added.final_price || added.price) / 100 * quantity,
            currency: state.currency,
          });
        }
        // GA4 add_to_cart
        if (typeof gtag === 'function') {
          gtag('event', 'add_to_cart', {
            currency: state.currency,
            value: (added.final_price || added.price) / 100 * quantity,
            items: [{
              item_id: String(variantId),
              item_name: added.product_title || added.title,
              item_variant: added.variant_title,
              price: (added.final_price || added.price) / 100,
              quantity,
            }],
          });
        }

        // Auto-apply saved discount
        const savedCode = localStorage.getItem('cart-discount-code');
        if (savedCode) applyDiscount(savedCode).catch(() => {});
      } catch (e) {
        console.error('Failed to add item:', e);
      } finally {
        isLoading = false; render();
      }
    }

    async function updateQuantity(key, quantity) {
      if (quantity <= 0) return removeItem(key);
      isLoading = true; render();
      try {
        await fetch(ROUTES.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        await fetchCart();
      } catch (e) { console.error('Failed to update quantity:', e); }
      finally { isLoading = false; render(); }
    }

    async function removeItem(key) {
      isLoading = true; render();
      try {
        await fetch(ROUTES.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        await fetchCart();
      } catch (e) { console.error('Failed to remove item:', e); }
      finally { isLoading = false; render(); }
    }

    async function applyDiscount(code) {
      try {
        await fetch(ROUTES.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code }),
        });
        await fetchCart();
        render();
      } catch (e) { console.warn('Failed to apply discount:', e); }
    }

    function open() { drawerOpen = true; render(); }
    function close() { drawerOpen = false; render(); }

    function render() {
      // Badges (desktop + mobile header)
      ['cart-badge', 'cart-badge-mobile'].forEach(id => {
        const badge = document.getElementById(id);
        if (badge) {
          badge.textContent = String(state.item_count || 0);
          badge.classList.toggle('hidden', !state.item_count);
        }
      });

      // Drawer
      const overlay = document.getElementById('cart-drawer-overlay');
      const drawer = document.getElementById('cart-drawer');
      if (overlay && drawer) {
        if (drawerOpen) {
          overlay.classList.remove('hidden');
          document.body.style.overflow = 'hidden';
          requestAnimationFrame(() => {
            overlay.querySelector('.cart-backdrop')?.classList.add('opacity-100');
            drawer.classList.remove('translate-x-full');
          });
        } else {
          overlay.querySelector('.cart-backdrop')?.classList.remove('opacity-100');
          drawer.classList.add('translate-x-full');
          document.body.style.overflow = '';
          setTimeout(() => overlay.classList.add('hidden'), 300);
        }
      }

      // Sticky offer bar: hidden while the drawer is open, and stays
      // hidden once dismissed for the session
      const offerBar = document.getElementById('sticky-bar');
      if (offerBar) {
        let offerDismissed = false;
        try { offerDismissed = sessionStorage.getItem('v7-offer-dismissed') === '1'; } catch {}
        offerBar.classList.toggle('hidden', drawerOpen || offerDismissed);
      }

      // Content
      const content = document.getElementById('cart-drawer-content');
      if (!content) return;

      const items = state.items || [];
      const total = (state.items_subtotal_price || 0) / 100;
      const toFree = Math.max(FREE_SHIP - total, 0);
      const progress = Math.min((total / FREE_SHIP) * 100, 100);

      if (items.length === 0) {
        content.innerHTML = `
          <div class="flex-1 flex items-center justify-center">
            <div class="text-center">
              <svg class="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>
              <p class="text-muted-foreground text-lg font-medium">Your cart is empty</p>
              <p class="text-sm text-muted-foreground mt-1">Add some products to get started</p>
              <button onclick="SunBrims.cart.close()" type="button" class="mt-4 inline-flex items-center justify-center h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">Continue Shopping</button>
            </div>
          </div>`;
        return;
      }

      const shippingBar = toFree > 0
        ? `<div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
            <span class="text-sm text-muted-foreground">Add <strong class="text-sunbrim-ink">$${toFree.toFixed(2)}</strong> more for free shipping!</span>
          </div>
          <div class="w-full bg-muted rounded-full h-2"><div class="bg-primary h-2 rounded-full transition-all" style="width:${progress}%"></div></div>`
        : `<div class="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
            <span class="text-sm font-medium">🎉 You've unlocked FREE shipping!</span>
          </div>`;

      const itemsHtml = items.map(item => {
        const opts = (item.options_with_values || []).filter(o => o.value !== 'Default Title').map(o => o.value).join(' • ');
        const img = item.featured_image?.url || item.image;
        const imgHtml = img ? `<img src="${img}" alt="${item.product_title}" class="w-full h-full object-cover">` : '';
        const price = (item.final_price || item.price) / 100;
        return `<div class="flex items-center gap-3 p-2 sm:p-3 rounded-lg bg-muted/30 border border-border/50">
          <div class="w-14 h-14 sm:w-16 sm:h-16 bg-secondary rounded-lg overflow-hidden flex-shrink-0">${imgHtml}</div>
          <div class="flex-1 min-w-0">
            <h4 class="font-medium text-sm line-clamp-2">${item.product_title}</h4>
            ${opts ? `<p class="text-xs text-muted-foreground mt-0.5">${opts}</p>` : ''}
            <p class="font-semibold text-sunbrim-ink mt-1">$${price.toFixed(2)}</p>
          </div>
          <div class="flex flex-col items-center gap-2 flex-shrink-0">
            <div class="flex items-center gap-0.5 bg-background rounded-md border">
              <button type="button" onclick="SunBrims.cart.updateQty('${item.key}', ${item.quantity - 1})" class="h-7 w-7 flex items-center justify-center hover:bg-muted transition-colors rounded-l-md">
                <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
              </button>
              <span class="w-5 text-center text-sm font-medium">${item.quantity}</span>
              <button type="button" onclick="SunBrims.cart.updateQty('${item.key}', ${item.quantity + 1})" class="h-7 w-7 flex items-center justify-center hover:bg-muted transition-colors rounded-r-md">
                <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
              </button>
            </div>
            <button type="button" onclick="SunBrims.cart.remove('${item.key}')" class="h-6 px-2 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors">
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
            </button>
          </div>
        </div>`;
      }).join('');

      const discountDismissed = localStorage.getItem('cart-discount-dismissed') === '1';
      const discountClaimed = localStorage.getItem('cart-discount-claimed') === '1';
      let discountCardHtml = '';
      if (!discountDismissed && SB.enableDiscountCard !== false) {
        if (discountClaimed) {
          discountCardHtml = `
            <div id="cart-discount-card" class="px-4 py-3 rounded-xl border border-primary/30 bg-primary/5 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="text-lg">🎉</span>
                <p class="text-sm font-medium text-foreground">Your 10% discount code has been sent!</p>
              </div>
              <button type="button" onclick="localStorage.setItem('cart-discount-dismissed','1');document.getElementById('cart-discount-card')?.remove()" class="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground rounded-full hover:bg-muted transition-colors flex-shrink-0">
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>`;
        } else {
          discountCardHtml = `
            <div id="cart-discount-card" class="p-3 sm:p-4 rounded-xl border-2 border-primary bg-primary/10 relative shadow-sm">
              <button type="button" onclick="localStorage.setItem('cart-discount-dismissed','1');document.getElementById('cart-discount-card')?.remove()" class="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground rounded-full hover:bg-muted transition-colors">
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
              <p class="font-bold text-sm sm:text-base text-foreground leading-tight pr-6 mb-2">🎉 Get 10% off this order</p>
              <div id="cart-discount-form">
                <div class="flex gap-2">
                  <input id="cart-discount-email" type="email" placeholder="you@email.com" class="flex-1 h-9 sm:h-10 rounded-lg border border-border bg-background px-3 text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary min-w-0">
                  <button type="button" id="cart-discount-submit" onclick="SunBrims.cart.claimDiscount()" class="h-9 sm:h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors whitespace-nowrap flex items-center justify-center shadow-sm shrink-0">
                    Get Code
                  </button>
                </div>
                <p id="cart-discount-error" class="hidden text-xs text-destructive mt-1"></p>
                <p id="cart-discount-success" class="hidden text-xs text-green-600 mt-1"></p>
              </div>
            </div>`;
        }
      }

      content.innerHTML = `
        <div class="py-4 border-b border-border">${shippingBar}</div>
        <div class="flex-1 overflow-y-auto pr-2 min-h-0 pt-4">
          <div class="space-y-3">${itemsHtml}</div>
        </div>
        <div class="flex-shrink-0 space-y-3 pt-4 border-t mt-4 bg-background">
          ${discountCardHtml}
          <div class="space-y-2">
            <div class="flex justify-between text-sm">
              <span class="text-muted-foreground">Subtotal</span>
              <span class="font-medium">$${total.toFixed(2)}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-muted-foreground">Shipping</span>
              <span class="font-medium ${total >= FREE_SHIP ? 'text-green-600' : ''}">${total >= FREE_SHIP ? 'FREE' : 'Calculated at checkout'}</span>
            </div>
          </div>
          <div class="flex justify-between items-center pt-2 border-t border-border">
            <span class="text-lg font-semibold">Total</span>
            <span class="text-2xl font-bold">$${total.toFixed(2)} ${state.currency}</span>
          </div>
          <button type="button" onclick="SunBrims.cart.checkout()" ${isLoading ? 'disabled' : ''} class="w-full inline-flex items-center justify-center h-12 rounded-md bg-primary text-primary-foreground font-semibold shadow-md hover:bg-primary/90 transition-colors">
            ${isLoading
              ? '<svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>'
              : '<svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>Proceed to Checkout'}
          </button>
          <p class="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
            <svg class="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
            Secure checkout powered by Shopify
          </p>
        </div>`;
    }

    async function claimDiscount() {
      const input = document.getElementById('cart-discount-email');
      const err = document.getElementById('cart-discount-error');
      const ok = document.getElementById('cart-discount-success');
      const btn = document.getElementById('cart-discount-submit');
      if (!input || !err || !ok || !btn) return;
      const email = input.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        err.textContent = 'Please enter a valid email address';
        err.classList.remove('hidden'); ok.classList.add('hidden');
        return;
      }
      err.classList.add('hidden');
      btn.innerHTML = '<svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';
      btn.disabled = true;
      try {
        const res = await fetch(API_BASE + '/shop/discount', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await res.json();
        if (data.code) {
          localStorage.setItem('cart-discount-claimed', '1');
          localStorage.setItem('cart-discount-code', data.code);
          ok.innerHTML = `Your code: <strong class="font-mono text-sm">${data.code}</strong>. It will be applied automatically at checkout!`;
          ok.classList.remove('hidden');
          input.classList.add('hidden'); btn.classList.add('hidden');
          applyDiscount(data.code).catch(() => {});
        } else {
          err.textContent = data.error || 'Something went wrong. Try the signup in the footer instead.';
          err.classList.remove('hidden');
          btn.innerHTML = 'Get Code'; btn.disabled = false;
        }
      } catch {
        err.textContent = 'Network error. Please try again.';
        err.classList.remove('hidden');
        btn.innerHTML = 'Get Code'; btn.disabled = false;
      }
    }

    function checkout() {
      // Meta Pixel InitiateCheckout
      if (typeof fbq === 'function') {
        const value = (state.items_subtotal_price || 0) / 100;
        fbq('track', 'InitiateCheckout', {
          content_ids: state.items.map(i => String(i.variant_id)),
          content_type: 'product',
          value,
          currency: state.currency,
          num_items: state.item_count,
        });
      }
      // GA4 begin_checkout
      if (typeof gtag === 'function') {
        gtag('event', 'begin_checkout', {
          currency: state.currency,
          value: (state.items_subtotal_price || 0) / 100,
          items: state.items.map(i => ({
            item_id: String(i.variant_id),
            item_name: i.product_title,
            item_variant: i.variant_title,
            price: (i.final_price || i.price) / 100,
            quantity: i.quantity,
          })),
        });
      }
      // Note: server-side Meta CAPI events used to be POSTed to
      // api.sunbrims.com.au/tracking/meta here. Shopify's native Meta Pixel +
      // Conversions API integration (set up via Settings → Customer events)
      // now forwards events server-side automatically, so we just rely on
      // Shopify + the client-side fbq('track', ...) above.

      window.location.href = ROUTES.cart + '/checkout';
    }

    function init() {
      // Wire toggle
      document.getElementById('cart-toggle')?.addEventListener('click', open);
      document.getElementById('cart-drawer-close')?.addEventListener('click', close);
      document.querySelector('#cart-drawer-overlay .cart-backdrop')?.addEventListener('click', close);
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawerOpen) close(); });

      // Intercept any [data-add-to-cart] form on the page
      document.addEventListener('submit', async e => {
        const form = e.target.closest('form[data-add-to-cart]');
        if (!form) return;
        e.preventDefault();
        const idEl = form.querySelector('[name="id"]');
        const qtyEl = form.querySelector('[name="quantity"]');
        if (!idEl?.value) return;
        await addItem({ variantId: idEl.value, quantity: parseInt(qtyEl?.value || '1', 10) });
      });

      fetchCart().then(render);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') fetchCart().then(render);
      });
    }

    return { init, add: addItem, updateQty: updateQuantity, remove: removeItem, applyDiscount, claimDiscount, checkout, open, close, get state() { return state; } };
  })();

  SB.cart = cart;

  // ── 9. FAQ accordion ─────────────────────────────────────────────
  function initFaqAccordions() {
    document.querySelectorAll('.faq-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const parent = btn.closest('.rounded-xl');
        const content = parent?.querySelector('.faq-content');
        const chevron = btn.querySelector('.faq-chevron');
        const isOpen = !content?.classList.contains('hidden');
        document.querySelectorAll('.faq-content').forEach(c => c.classList.add('hidden'));
        document.querySelectorAll('.faq-chevron').forEach(c => c.classList.remove('rotate-180'));
        if (!isOpen && content) {
          content.classList.remove('hidden');
          chevron?.classList.add('rotate-180');
        }
      });
    });
  }

  // ── 10. Hero review carousel (reads baked #baked-reviews JSON) ───
  // Reviews are baked into the theme at deploy time by `npm run fetch-reviews`
  // and rendered server-side, so there's no fetch and no fallback flash.
  function initHeroReviewCarousel() {
    const textEl = document.getElementById('hero-review-text');
    const authorEl = document.getElementById('hero-review-author');
    const dotsEl = document.getElementById('hero-review-dots');
    const dataEl = document.getElementById('baked-reviews');
    if (!textEl || !authorEl || !dotsEl || !dataEl) return;

    let reviews = [];
    try { reviews = JSON.parse(dataEl.textContent || '[]'); } catch { return; }
    reviews = reviews.filter(r => r.body).slice(0, 8);
    if (reviews.length === 0) return;

    let current = 0;
    const show = i => {
      const r = reviews[i];
      textEl.style.opacity = '0';
      authorEl.style.opacity = '0';
      setTimeout(() => {
        textEl.textContent = `"${r.body}"`;
        authorEl.textContent = `- ${r.name || 'Happy Customer'}`;
        textEl.style.opacity = '1';
        authorEl.style.opacity = '1';
      }, 200);
      dotsEl.querySelectorAll('button').forEach((d, idx) => {
        d.className = idx === i
          ? 'w-4 h-1.5 rounded-full bg-primary transition-all'
          : 'w-1.5 h-1.5 rounded-full bg-white/30 hover:bg-white/50 transition-all';
      });
    };

    dotsEl.innerHTML = '';
    reviews.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.addEventListener('click', () => { current = i; show(i); });
      dotsEl.appendChild(dot);
    });
    textEl.style.transition = 'opacity 0.2s';
    authorEl.style.transition = 'opacity 0.2s';
    show(0);
    setInterval(() => { current = (current + 1) % reviews.length; show(current); }, 5000);
  }

  // ── Init ─────────────────────────────────────────────────────────
  function init() {
    initAnnouncementRotator();
    initOfferBar();
    initSearchOverlay();
    initFitSearch();
    initMobileMenu();
    initMegaMenu();
    deobfuscate();
    initNewsletter();
    initFaqAccordions();
    initHeroReviewCarousel();
    cart.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
